// Fuzzy text matching for duplicate detection.
//
// Two main use cases:
//   1. Cross-room duplicate warnings (instructor view) — moderate threshold
//      (~0.82) so we catch typos and casing variants.
//   2. Auto-marking queued requests as played when Spotify confirms a song
//      played — stricter threshold (~0.88) since a false positive silently
//      vanishes the request from the list.
//
// Strategy:
//   1. Normalize both strings (lowercase, strip punctuation, collapse spaces).
//   2. Compare titles with Levenshtein-based similarity, normalized to [0,1].
//   3. For artists, take the MAX of:
//        a. Levenshtein-based similarity on the full string, AND
//        b. Token-overlap ratio after splitting by ",", "&", "feat", "ft",
//           "and" — so "Pitbull" matches "Pitbull, Flo Rida" cleanly.
//      This handles featured-artist variations without losing precision.
//
// Note: same-dance-different-titles (e.g. "rude dude" and "church" being the
// same line dance) is NOT handled here. That requires a curated alias map.

export function normalize(s) {
  if (!s) return ''
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip diacritical marks (NFKD decomposed)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function levenshtein(a, b) {
  if (a === b) return 0
  if (!a) return b.length
  if (!b) return a.length
  const m = a.length
  const n = b.length
  let prev = new Array(n + 1)
  let curr = new Array(n + 1)
  for (let j = 0; j <= n; j++) prev[j] = j
  for (let i = 1; i <= m; i++) {
    curr[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost)
    }
    ;[prev, curr] = [curr, prev]
  }
  return prev[n]
}

export function similarity(a, b) {
  const na = normalize(a)
  const nb = normalize(b)
  if (!na && !nb) return 1
  const dist = levenshtein(na, nb)
  const maxLen = Math.max(na.length, nb.length)
  return maxLen === 0 ? 1 : 1 - dist / maxLen
}

/**
 * Split an artist string into individual artist tokens. Handles common
 * separators: commas, ampersands, "feat", "ft", "and".
 * Examples:
 *   "Pitbull, Flo Rida"     -> ["pitbull", "flo rida"]
 *   "Lorde feat. SZA"       -> ["lorde", "sza"]
 *   "Beyoncé & Jay-Z"       -> ["beyonce", "jay z"]
 */
function splitArtistTokens(s) {
  if (!s) return []
  // Split BEFORE normalization — normalize() strips punctuation including
  // commas/&, so we'd lose all the separators if we normalized first.
  return s
    .toLowerCase()
    .split(/\s*(?:,|&|\sfeat\.?\s|\sft\.?\s|\sand\s|\swith\s|\svs\.?\s|\sx\s)\s*/)
    .map((t) => normalize(t))
    .filter(Boolean)
}

/**
 * Token-overlap ratio between two artist strings. Returns the fraction of
 * `a`'s tokens that have a fuzzy-matching counterpart in `b`. So "Pitbull"
 * vs "Pitbull, Flo Rida" returns 1.0 (all of a's tokens are in b), while
 * "Pitbull, Flo Rida" vs "Pitbull" returns 0.5 (only 1 of 2 a-tokens matches).
 *
 * For our use we take the MAX in both directions to be robust to either
 * order — see `artistAffinity` below.
 */
function artistTokenOverlap(a, b) {
  const tokensA = splitArtistTokens(a)
  const tokensB = splitArtistTokens(b)
  if (tokensA.length === 0 || tokensB.length === 0) return 0
  let matches = 0
  for (const tA of tokensA) {
    for (const tB of tokensB) {
      // Per-token similarity threshold of 0.85 catches "Beyoncé" vs
      // "Beyonce" but rejects "Pitbull" vs "Eminem".
      if (similarity(tA, tB) >= 0.85) {
        matches++
        break
      }
    }
  }
  return matches / tokensA.length
}

/**
 * Combined artist similarity score in [0, 1]. Uses the max of:
 *   - Whole-string Levenshtein similarity, AND
 *   - Bidirectional token overlap (so "Pitbull" matches "Pitbull, Flo Rida"
 *     in either direction).
 */
export function artistAffinity(a, b) {
  if (!a || !b) return 0
  const stringScore = similarity(a, b)
  const overlapAB = artistTokenOverlap(a, b)
  const overlapBA = artistTokenOverlap(b, a)
  return Math.max(stringScore, overlapAB, overlapBA)
}

/**
 * Check whether a candidate request matches anything in the `played` array.
 * Returns the matching played-tonight entry, or null if no match.
 *
 * @param candidate  { song_title, artist, spotify_track_id? }
 * @param played     array of { song_title, artist, room, spotify_track_id, played_at }
 * @param threshold  similarity threshold. Default 0.82 (moderate, used for
 *                   instructor duplicate warnings). Use ~0.88 for stricter
 *                   "auto-mark as played" matching where false positives
 *                   silently vanish requests.
 */
export function findDuplicate(candidate, played, threshold = 0.82) {
  if (!candidate?.song_title) return null

  // 1. Spotify track ID — gold standard. Available when the request was
  //    submitted via the search-as-you-type picker, or for entries the
  //    poller writes to played_tonight.
  if (candidate.spotify_track_id) {
    const m = played.find((p) => p.spotify_track_id === candidate.spotify_track_id)
    if (m) return m
  }

  // 2. Fuzzy title match, with artist as boost. Title is the dominant signal
  //    (70%); artist token overlap nudges a near-miss over the threshold or
  //    confirms a tight match.
  let bestMatch = null
  let bestScore = 0
  for (const p of played) {
    const titleScore = similarity(candidate.song_title, p.song_title)
    let score = titleScore
    if (candidate.artist && p.artist) {
      const artistScore = artistAffinity(candidate.artist, p.artist)
      score = titleScore * 0.7 + artistScore * 0.3
    }
    if (score > bestScore) {
      bestScore = score
      bestMatch = p
    }
  }
  return bestScore >= threshold ? bestMatch : null
}
